/**
 * Core Pagination Hook
 * Manages pagination state with URL sync and localStorage persistence
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'wouter';
import {
  UsePaginationConfig,
  UsePaginationReturn,
  DEFAULT_PAGINATION_CONFIG,
} from '@/lib/pagination/types';
import {
  getPaginationParams,
  updatePaginationParams,
  loadPaginationState,
  savePaginationState,
  clampPage,
} from '@/lib/pagination/utils';

export function usePagination(
  config: UsePaginationConfig = {}
): UsePaginationReturn & { limitOptions: number[] } {
  const {
    initialPage = DEFAULT_PAGINATION_CONFIG.initialPage,
    initialLimit = DEFAULT_PAGINATION_CONFIG.initialLimit,
    limitOptions = DEFAULT_PAGINATION_CONFIG.limitOptions,
    persistState = DEFAULT_PAGINATION_CONFIG.persistState,
    storageKey = 'default',
    syncWithUrl = DEFAULT_PAGINATION_CONFIG.syncWithUrl,
  } = config;

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL, localStorage, or defaults
  const getInitialState = useCallback(() => {
    if (syncWithUrl) {
      const urlParams = getPaginationParams(searchParams);
      if (urlParams.page > 1 || urlParams.limit !== initialLimit) {
        return urlParams;
      }
    }

    if (persistState) {
      const stored = loadPaginationState(storageKey);
      if (stored) {
        return {
          page: stored.page || initialPage,
          limit: stored.limit || initialLimit,
        };
      }
    }

    return { page: initialPage, limit: initialLimit };
  }, [searchParams, syncWithUrl, persistState, storageKey, initialPage, initialLimit]);

  const [state, setState] = useState(getInitialState);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Update state when URL changes (for browser back/forward)
  useEffect(() => {
    if (syncWithUrl) {
      const urlParams = getPaginationParams(searchParams);
      setState(urlParams);
    }
  }, [searchParams, syncWithUrl]);

  // Persist state to localStorage
  useEffect(() => {
    if (persistState) {
      savePaginationState(storageKey, state.page, state.limit);
    }
  }, [state, persistState, storageKey]);

  // Update URL when state changes
  const updateUrl = useCallback(
    (page: number, limit: number) => {
      if (syncWithUrl) {
        const newParams = updatePaginationParams(searchParams, page, limit);
        setSearchParams(newParams, { replace: true });
      }
    },
    [searchParams, setSearchParams, syncWithUrl]
  );

  // Action: Set page
  const setPage = useCallback(
    (page: number) => {
      const clampedPage = clampPage(page, totalPages);
      setState((prev) => ({ ...prev, page: clampedPage }));
      updateUrl(clampedPage, state.limit);
    },
    [totalPages, state.limit, updateUrl]
  );

  // Action: Set page size
  const setPageSize = useCallback(
    (limit: number) => {
      // When changing page size, calculate which page to show to maintain position
      const currentFirstItem = (state.page - 1) * state.limit + 1;
      const newPage = Math.max(1, Math.ceil(currentFirstItem / limit));

      setState({ page: newPage, limit });
      updateUrl(newPage, limit);
    },
    [state.page, state.limit, updateUrl]
  );

  // Navigation actions
  const nextPage = useCallback(() => {
    if (state.page < totalPages) {
      setPage(state.page + 1);
    }
  }, [state.page, totalPages, setPage]);

  const previousPage = useCallback(() => {
    if (state.page > 1) {
      setPage(state.page - 1);
    }
  }, [state.page, setPage]);

  const firstPage = useCallback(() => {
    setPage(1);
  }, [setPage]);

  const lastPage = useCallback(() => {
    setPage(totalPages);
  }, [totalPages, setPage]);

  // Computed properties
  const hasNext = state.page < totalPages;
  const hasPrevious = state.page > 1;
  const isFirstPage = state.page === 1;
  const isLastPage = state.page === totalPages;

  // Public method to update pagination meta
  const updatePaginationMeta = useCallback(
    (meta: { totalPages: number; totalCount: number }) => {
      setTotalPages(meta.totalPages);
      setTotalCount(meta.totalCount);

      // Clamp current page if it exceeds new total
      if (state.page > meta.totalPages && meta.totalPages > 0) {
        setPage(meta.totalPages);
      }
    },
    [state.page, setPage]
  );

  return {
    // State
    currentPage: state.page,
    pageSize: state.limit,
    totalPages,
    totalCount,
    hasNext,
    hasPrevious,
    isFirstPage,
    isLastPage,

    // Actions
    setPage,
    setPageSize,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    canGoNext: hasNext,
    canGoPrevious: hasPrevious,

    // Config
    limitOptions,

    // Meta update
    updatePaginationMeta,
  };
}
